import { Link } from "react-router-dom"
import PropTypes from "prop-types"
import { Box, Divider, Drawer, useMediaQuery, Typography } from "@mui/material"
import { NavItem } from "./nav-item"
import logo from "assets/new-logo-white.svg"
import { logout } from "utils/external-login-helper"
import { IconHome, IconSales, IconInventory, IconInbound, IconReturns, IconAnalytics, IconOutbound, IconInboundArrow, IconFreight, IconHelp, IconLogout, IconVersion } from "./nav-icons"
import { rootStore } from "stores/root-store"
import { helpUrl } from "shared/constants/endpoint"
import { MenuContainerBox, SidebarContainerBox, LogoContainerBox, SidebarMenuScrollBox } from "./styles"
import { SettingConstant } from "shared/constants/setting-constant"

export const MainSidebar = props => {
  const { open, onClose } = props
  const lgUp = useMediaQuery(theme => theme.breakpoints.up("lg"), {
    defaultMatches: true,
    noSsr: false,
  })

  const items = []

  items.push({
    icon: <IconHome />,
    title: "Home",
    href: "/",
    id: "home",
  })

  if (rootStore.shouldShowSaleOrder)
    items.push({
      icon: <IconSales />,
      title: "Sales order",
      href: "/sales-order",
      id: "sales_order",
    })

  if (rootStore.shouldShowInventory)
    items.push({
      icon: <IconInventory />,
      title: "Inventory",
      id: "inventory_group",
      children: [
        {
          title: "Products",
          href: "/products",
          id: "products",
        },
        {
          title: "Inventory",
          href: "/inventory",
          id: "inventory",
        },
        {
          title: "Bill of materials",
          href: "/bom",
          id: "bom",
        },
        {
          title: "DG Item Profile",
          href: "/dg-item-profile",
          id: "dg-item-profile",
        },
      ],
    })

  if (rootStore.shouldShowInboundDelivery)
    items.push({
      icon: <IconInbound />,
      title: "Inbound delivery",
      href: "/inbound-delivery",
      id: "inbound_delivery",
    })

  if (rootStore.shouldShowReturns)
    items.push({
      icon: <IconReturns />,
      title: "Returns",
      href: "/return",
      id: "return",
    })

  items.push({
    icon: <IconAnalytics />,
    title: "Analytics",
    id: "analytics_group",
    children: [
      {
        icon: <IconOutbound />,
        title: "Outbound",
        href: "/analytics/outbound",
        id: "analytics_outbound",
      },
      {
        icon: <IconInboundArrow />,
        title: "Inbound",
        href: "/analytics/inbound",
        id: "analytics_inbound",
      },
    ],
  })

  const signOut = () => {
    rootStore.reset()

    logout()
  }

  const sectionLabelSx = {
    color: "#868EA1",
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    pl: 1.5,
    pr: 1.5,
    pt: 1.5,
    pb: 0.75,
  }

  const content = (
    <SidebarContainerBox>
      <LogoContainerBox>
        <img src={logo} alt="eStore Logistics" />
      </LogoContainerBox>
      <MenuContainerBox>
        <SidebarMenuScrollBox>
          <Typography sx={sectionLabelSx}>Menu</Typography>
          {items.map(item => (
            <NavItem id={item.id} key={item.title} icon={item.icon} href={item.href} title={item.title.toUpperCase()} items={item.children} />
          ))}
        </SidebarMenuScrollBox>

        <Box sx={{ flexShrink: 0, pt: 0.5 }}>
          <Typography sx={sectionLabelSx}>Support</Typography>
          <NavItem id="freight_enquiry" icon={<IconFreight />} title={"Freight enquiry"} href={"/freight-enquiry"} />
          <NavItem id="help" icon={<IconHelp />} title={"Help"} href={helpUrl} target="_blank" />

          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mx: 1.5, my: 1 }} />

          <NavItem id="logout" icon={<IconLogout />} href={signOut} title={"Logout"} danger />

          {/* Software version — non-interactive, styled like a nav item so the
              customer can see which Express version they're on. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1, color: "#8A93A7", "& svg": { width: 20, height: 20 } }}>
            <IconVersion />
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Version {SettingConstant.APP_VERSION}</Typography>
          </Box>
        </Box>
      </MenuContainerBox>
    </SidebarContainerBox>
  )

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            backgroundColor: "#14151C",
            color: "#FFFFFF",
            width: 248,
            borderRight: "none",
          },
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    )
  }

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          backgroundColor: "neutral.900",
          color: "#FFFFFF",
          width: 220,
        },
      }}
      sx={{ zIndex: theme => theme.zIndex.appBar + 100 }}
      variant="temporary"
    >
      {content}
    </Drawer>
  )
}

MainSidebar.propTypes = {
  onClose: PropTypes.func,
  open: PropTypes.bool,
}
